/** object representation for style sizings, good to know the unit from the measurement separately */
export interface SplitMeasurement {
  measurement: string;
  unit: string;
}

const unitSplitterRegex = /(-?[\d.]+)([a-z%]*)/;

/**
 * Filters out styles that are not whitelisted from a style string.
 * If whitelistedStyle is empty then empty string will be returned.
 * @param styleStr The original style string.
 * @param whitelistedStyles A list of styles that acceptable in styleStr.
 * @returns returns styleStr but only with styles that are in whiltelistedStyles.
 */
export function filterStyleString(styleStr: string, whitelistedStyles: string[]): string {
  const styleDict = parseStyles(styleStr);
  let filteredStyleStr = '';
  for (const [key, value] of Object.entries(styleDict)) {
    if (whitelistedStyles.includes(key)) {
      filteredStyleStr += key + ': ' + value + '; ';
    }
  }
  return filteredStyleStr.trim();
}

/**
 * Used to more easily update a style string with new values.
 * @param styleStr The original style string.
 * @returns A dictionary that represents the styleStr.
 */
export function parseStyles(styleStr: string): Dictionary<string> {
  const styleDict = {};
  if (styleStr) {
    styleStr.split(';').forEach((declaration) => {
      const [name, value] = declaration.split(':').map(str => str.trim());
      if (name && value) {
        styleDict[name] = value;
      }
    });
  }

  return styleDict;
}

/**
 * Parses a style value string by checking for !important priority.
 * @param styleValue The style value string to parse.
 * @returns An object with the value and optional priority.
 */
export function parseStyleValue(styleValue: string): { value: string; priority?: string } {
  const match = /!\s*important\s*$/i.exec(styleValue);
  if (match) {
    return { value: styleValue.slice(0, match.index).trim(), priority: 'important' };
  } else {
    return { value: styleValue };
  }
}

/**
 * Used to change a style dict to a string to use with prosemirror.
 * @param styleDict The keys/values in this dict are what makes up the return value.
 * @returns styleDict as a string.
 */
export function stringifyStyles(styleDict: Dictionary): string {
  let styleString = '';

  for (const property in styleDict) {
    if (styleDict.hasOwnProperty(property) && typeof styleDict[property] === 'string') {
      styleString += property + ': ' + styleDict[property] + '; ';
    }
  }

  return styleString.trim();
}

/**
 * Filters out styles that are not whitelisted from a dictionary of styles.
 * @param styleDict The dictionary of styles to filter.
 * @param whitelistedStyles The dictionary of whitelisted styles. The keys are the style names. The values are a regex of acceptable values.
 * @returns A dictionary of styles that are in the whitelisted styles.
 */
export function filterStyles(styles: Dictionary<string>, whitelistedStyles: Dictionary<RegExp>): Dictionary<string> {
  if (styles) {
    const filteredStyles: Dictionary<string> = {};

    for (const [styleName, styleValue] of Object.entries(styles)) {
      const regex = whitelistedStyles[styleName];
      if (regex?.test(styleValue)) {
        filteredStyles[styleName] = styleValue;
      }
    }

    return filteredStyles;
  }
}


/**
 * Merges two style dictionaries together. Any null, undefined, or empty string values will be removed.
 * Any null, undefined, or empty string values in style2 will overwrite the values in style1.
 * @param style1 The first style dictionary to merge.
 * @param style2 The second style dictionary to merge.
 * @returns A new dictionary that is the result of merging style1 and style2.
 */
export function mergeStyles(style1: Dictionary, style2: Dictionary): Dictionary {
  const styles = {
    ...style1,
    ...style2
  };

  // Filter out any null, undefined, or empty string values
  return removeEmptyStyles(styles);
}

/**
 * Removes null, undefined, or empty string values.
 * @param style The style dictionary
 * @returns A new dictionary with empty styles removed
 */
export function removeEmptyStyles(style: Dictionary): Dictionary {
  // Filter out any null, undefined, or empty string values
  const filteredStyle = Object.fromEntries(Object.entries(style ?? []).filter(([key, value]) => {
    return value !== null && typeof value !== 'undefined' && value !== '';
  }));

  // If the style is empty then use undefined
  return Object.keys(filteredStyle).length > 0 ? filteredStyle : undefined;
}

/**
 * Splits a string value that is made out of a length and a unit.
 * If the string is empty will return undefined.
 * If the string is not empty but failed to split it to length and a unit, it returns the original string in the measurement field.
 * @param measurementStr A string that is number followed by a unit such as (px, em , in, mm, etc), or just a string.
 * @return returns SplitMeasurement which is a representation of measurementStr but split
 * based on its numeric value and unit. So for instance splitMeasurementFromUnit('30px') -> {measurement: 30, unit: 'px'};
 * It returns the original string if the the split failed.
 */
export function splitMeasurementFromUnit(measurementStr: string): SplitMeasurement {
  if (measurementStr) {
    const res = measurementStr.match(unitSplitterRegex);
    if (res && res.index === 0) {
      return { measurement: res[1], unit: res[2] };
    } else {
      return { measurement: measurementStr, unit: null };
    }
  }
}

/**
 * Parses a class string into an array of class names.
 * @param classString The class string to parse.
 * @returns An array of class names.
 */
export function parseClassString(classString: string): string[] {
  if (typeof classString === 'string') {
    return classString.split(' ').map(name => name.trim()).filter(name => name);
  } else {
    return [];
  }
}

/**
 * Stringifies an array of class names into a class string.
 * @param classList The array of class names.
 * @returns A space-separated class string.
 */
export function stringifyClassList(classList: string[]): string {
  return Array.isArray(classList) ? classList.join(' ') : '';
}

/**
 * Strips the url(...) function from around a URL string.
 * @param value The value to strip.
 * @returns The stripped URL or undefined if the value is not a valid url(...) function.
 */
export function stripStyleUrlFunction(value: string): string {
  if (!value) {
    return;
  }

  value = value.trim();

  const outerMatch = /^url\(\s*(.*?)\s*\)$/i.exec(value);
  if (!outerMatch) {
    return;
  }

  let inner = outerMatch[1].trim();
  if (inner.length === 0) {
    return;
  }

  const first = inner[0];
  const last = inner[inner.length - 1];
  const startsWithQuote = first === '"' || first === '\'';
  const endsWithQuote = last === '"' || last === '\'';

  // If a quote is used, it must be a matching outer pair.
  if (startsWithQuote) {
    if (inner.length < 2 || last !== first) {
      return;
    }
    inner = inner.slice(1, -1).trim();
  } else if (endsWithQuote) {
    // Reject stray trailing quotes like url(foo")
    return;
  }

  if (inner.length === 0) {
    return;
  }

  return inner;
}

/**
 * Strips matching outer quotes (single or double) from a string.
 * Useful for CSS variable values read via `getComputedStyle(...).getPropertyValue(...)`.
 *
 * Examples:
 * - `"foo"` -> `foo`
 * - `'foo'` -> `foo`
 * - ` foo ` -> `foo` (no quotes to strip)
 * - `"foo'` -> unchanged (mismatched quotes)
 * @param value The string value to strip quotes from.
 * @returns The string value without outer quotes.
 */
export function stripOuterQuotes(value: string): string {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (trimmed.length < 2) {
    return trimmed;
  }

  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  const isQuote = first === '"' || first === '\'';

  if (isQuote && last === first) {
    return trimmed.slice(1, -1);
  }

  return trimmed;
}
